import moment from 'moment-timezone';

import { Day } from '../interfaces/day';

/**
 * Returns the date of the specified weekday.
 *
 * @param {Date} date
 * @param {Day} day
 * @return {*}  {Date}
 */
function getDayOfWeek(date: Date, day: Day): Date {
  const momentDate = moment(date);
  return momentDate.day(day).toDate();
}

/**
 * Returns the date of monday of the specified weekday.
 *
 * @param {Date} date
 * @return {*}  {Date}
 */
function getMonday(date: Date): Date {
  return getDayOfWeek(date, Day.monday);
}

/**
 * Returns the date of friday of the specified weekday.
 *
 * @param {Date} date
 * @return {*}  {Date}
 */
function getFriday(date: Date): Date {
  return getDayOfWeek(date, Day.friday);
}

function formatDateTime(timestamp: Date | string) {
  return moment(timestamp).format('DD.MM.YYYY HH:mm');
}

function isoDayOfWeek(date: Date | string) {
  return moment(date).format('E');
}

function getStartOfWeek(date: Date | string, language?: string) {
  if (language) {
    moment.locale(language);
  }
  return moment(date).startOf('isoWeek');
}

function getEndOfWeek(date: Date | string, language?: string) {
  if (language) {
    moment.locale(language);
  }
  return moment(date).endOf('isoWeek');
}

function setDateAsUTC(date: Date | string) {
  return moment.utc(date);
}

function getIsoFormat(date: string | Date): string {
  const formatted = moment(date).format();
  return formatted;
}

function getLocalFormattedDate(date: string | Date) {
  return moment(date).format('L');
}

function getSmallLocalFormattedDateTime(date: string | Date) {
  return moment(date).format('lll');
}

function getDateRequestFormatted(date: string | Date | moment.Moment): string {
  return moment(date).format('yyyy-MM-DD');
}

/**
 *Checks if a date is UTC and returns a utc date.
 *
 * @param {(string | Date)} date
 * @return {*}
 */
function asUTC(date: string | Date | moment.Moment): string {
  let momentDate = moment(date);
  if (!momentDate.isUTC()) {
    momentDate = moment.utc(date);
  }
  return momentDate.format();
}

function getStartOfDayAsUTC(date: string | Date | moment.Moment): string {
  return asUTC(moment(date).startOf('day'));
}

function getEndOfDayAsUTC(date: string | Date | moment.Moment): string {
  return asUTC(moment(date).endOf('day'));
}

/**
 * Combines a date based on a given Date object or string
 * and a given time string with format: HH:mm / HH:mm:ss.
 *
 * @param {(Date | string)} date
 * @param {string} time Format: HH:mm / HH:mm:ss
 * @return {*}  {Date}
 */
function combineDateTime(date: Date | string, time: string): Date {
  const asDate = new Date(date);
  const splittedTime = time.split(':').map(t => Number.parseInt(t));

  return new Date(
    asDate.getFullYear(),
    asDate.getMonth(),
    asDate.getDate(),
    splittedTime.length > 0 ? splittedTime[0] : 0,
    splittedTime.length >= 2 ? splittedTime[1] : 0,
    splittedTime.length >= 3 ? splittedTime[2] : 0,
  );
}

const generateDateFromTime = (time: string): Date =>
  new Date(`1970-01-01T${time}`);

export {
  asUTC,
  combineDateTime,
  formatDateTime,
  generateDateFromTime,
  getDateRequestFormatted,
  getDayOfWeek,
  getEndOfWeek,
  getFriday,
  getIsoFormat,
  getLocalFormattedDate,
  getMonday,
  getSmallLocalFormattedDateTime,
  getStartOfDayAsUTC,
  getEndOfDayAsUTC,
  getStartOfWeek,
  isoDayOfWeek,
  setDateAsUTC,
};
